"use client";

/**
 * Bridges streamed tool calls to host-side execution.
 *
 * Watches copilot messages for the server-emitted `data-client-tool-request`
 * part — NOT AI-SDK's native `tool-input-available` event. The workflow emits
 * that part only *after* the HITL approval gate (if any) has resolved (ADR-050),
 * so by the time the client sees it the workflow is committed to executing and
 * the gate can't be raced. When the part appears, it runs the matching executor
 * and POSTs the result to the Magnus backend so the paused workflow webhook
 * resumes. Executors come from two sources, merged: tools registered on
 * `<MagnusProvider tools={...}>` (via `defineClientTool`) and any explicit
 * `adapters` passed here (e.g. the web app's Office.js adapters).
 *
 * The server emits the *effective* (already-unwrapped) inner tool name + args
 * in the data part — direct client tools as-is, and progressive-disclosure
 * proxies pre-resolved to the inner tool's namespaced identity (see
 * `executeClientSideTool` in `workflow.ts`). So the host adapter sees a uniform
 * `(toolName, args)` shape and ADR-043's wire contract stays unchanged; the
 * client no longer unwraps PD proxies itself (ADR-057).
 *
 * Generalizes `apps/web/hooks/use-client-tool-executor.tsx` (ADR-043).
 */
import { c as _c } from "react/compiler-runtime";
interface ClientToolRequestPayload {
  args: Record<string, unknown>;
  toolCallId: string;
  toolName: string;
}
function isClientToolRequestPart(part: unknown): part is {
  type: "data-client-tool-request";
  data: ClientToolRequestPayload;
} {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const p = part as {
    type?: unknown;
    data?: unknown;
  };
  if (p.type !== "data-client-tool-request") {
    return false;
  }
  const d = p.data as Partial<ClientToolRequestPayload> | undefined;
  return !!d && typeof d.toolCallId === "string" && typeof d.toolName === "string" && typeof d.args === "object" && d.args !== null;
}
import type { ClientToolResponse } from "@magnus/contract/client-tools";
import { hostToolWireName } from "@magnus/contract/host-tools";
import type { UIMessage } from "ai";
import { createContext, type ReactNode, use, useEffect, useMemo, useRef, useState } from "react";
import { postToolResult } from "../index";
import { useMagnusClient } from "./provider";

/**
 * Dev-only debug logging. Tree-shaken in production builds because bundlers
 * statically replace `process.env.NODE_ENV`. (Replace with `@workspace/logger`
 * once it ships a client-safe entry.) Restores the trace the app-local
 * executor had before the SDK extraction — without it, a stalled client tool
 * or a failed tool-results POST (which hangs the workflow webhook) leaves no
 * client-side breadcrumb.
 */
const DEV = process.env.NODE_ENV !== "production";
const debug = (...args: unknown[]) => {
  if (DEV) {
    console.log("[client-tool]", ...args);
  }
};
const debugError = (...args: unknown[]) => {
  if (DEV) {
    console.error("[client-tool]", ...args);
  }
};

/** A host-specific executor for a set of tool names (e.g. Excel, Word). */
export interface ClientToolExecutorAdapter {
  execute: (toolName: string, args: Record<string, unknown>) => Promise<unknown> | unknown;
  toolNames: ReadonlySet<string>;
}
export interface ClientToolState {
  completedAt?: number;
  error?: string;
  startedAt: number;
  status: "pending" | "executing" | "complete" | "error";
  toolCallId: string;
  toolName: string;
}
export interface UseMagnusToolsOptions {
  /** Additional host-specific adapters merged with provider-registered tools. */
  adapters?: ClientToolExecutorAdapter[];
  /** Fired after each tool finishes (success or error). */
  onToolComplete?: (state: ClientToolState) => void;
}
type ClientToolContextValue = {
  activeTools: ReadonlyMap<string, ClientToolState>;
};
const ClientToolContext = createContext<ClientToolContextValue | null>(null);
export function ClientToolProvider(t0) {
  const $ = _c(5);
  const {
    activeTools,
    children
  } = t0;
  let t1;
  if ($[0] !== activeTools) {
    t1 = {
      activeTools
    };
    $[0] = activeTools;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== children || $[3] !== value) {
    t2 = <ClientToolContext.Provider value={value}>{children}</ClientToolContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** Executor state for a tool call, or `undefined` outside a client-tool surface. */
export function useClientToolState(toolCallId: string): ClientToolState | undefined {
  return use(ClientToolContext)?.activeTools.get(toolCallId);
}
export function useMagnusTools(messages: UIMessage[] | undefined, options?: UseMagnusToolsOptions) {
  const {
    client,
    surface,
    tools
  } = useMagnusClient();

  // Adapter derived from provider-registered `defineClientTool` tools.
  const registeredAdapter = useMemo<ClientToolExecutorAdapter | null>(() => {
    if (tools.length === 0) {
      return null;
    }
    // Host-declared tools reach the model surface-namespaced
    // (`{surface}__{name}`, ADR-096), so incoming tool calls carry that wire
    // name. Alias every tool under both its plain and namespaced names — the
    // author only ever wrote the plain one.
    const byName = new Map(tools.map(t => [t.name, t]));
    if (surface) {
      for (const t_0 of tools) {
        byName.set(hostToolWireName(surface, t_0.name), t_0);
      }
    }
    return {
      toolNames: new Set(byName.keys()),
      execute: (toolName, args) => {
        const tool = byName.get(toolName);
        if (!tool) {
          throw new Error(`No executor registered for tool "${toolName}"`);
        }
        return tool.execute(args);
      }
    };
  }, [surface, tools]);
  const adapters = useMemo<ClientToolExecutorAdapter[]>(() => {
    const extra = options?.adapters ?? [];
    // Explicit `adapters` take precedence over provider-registered tools: the
    // executor picks the first adapter whose `toolNames` matches, so a caller
    // can override a provider tool's execution for the same name (e.g. the
    // chat-bound Outlook adapter overriding the provider's view-only entry).
    return registeredAdapter ? [...extra, registeredAdapter] : extra;
  }, [registeredAdapter, options?.adapters]);
  const processedRef = useRef<Set<string>>(new Set());
  const executionChainRef = useRef<Promise<void>>(Promise.resolve());
  const adaptersRef = useRef(adapters);
  adaptersRef.current = adapters;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const clientRef = useRef(client);
  clientRef.current = client;
  const [activeTools, setActiveTools] = useState<Map<string, ClientToolState>>(() => new Map());
  const upsert = useRef((toolCallId: string, patch: Partial<ClientToolState> & {
    toolName: string;
    startedAt: number;
  }) => {
    setActiveTools(prev => {
      const existing = prev.get(toolCallId);
      const updated: ClientToolState = {
        toolCallId,
        toolName: patch.toolName,
        status: patch.status ?? existing?.status ?? "pending",
        startedAt: patch.startedAt,
        completedAt: patch.completedAt ?? existing?.completedAt,
        error: patch.error ?? existing?.error
      };
      const next = new Map(prev);
      next.set(toolCallId, updated);
      return next;
    });
  }).current;

  // biome-ignore lint/correctness/useExhaustiveDependencies: must run only when `messages` changes; the executor is re-entrancy-guarded by processedRef
  useEffect(() => {
    if (!messages || messages.length === 0) {
      return;
    }
    for (const msg of messages) {
      if (msg.role !== "assistant") {
        continue;
      }
      for (const part of msg.parts) {
        if (!isClientToolRequestPart(part)) {
          continue;
        }
        // The server emits the effective (already-unwrapped) tool name + args
        // in the data part, so the adapter sees a uniform `(toolName, args)`
        // shape with no client-side PD unwrapping (ADR-057).
        const {
          toolCallId: toolCallId_0,
          toolName: toolName_0,
          args: args_0
        } = part.data;
        const adapter = adaptersRef.current.find(a => a.toolNames.has(toolName_0));
        if (!adapter) {
          continue;
        }
        if (processedRef.current.has(toolCallId_0)) {
          continue;
        }
        processedRef.current.add(toolCallId_0);
        executionChainRef.current = executionChainRef.current.then(() => runToolCall(adapter, toolCallId_0, toolName_0, args_0)).catch(() => {
          /* failures surface via tool state, not thrown */
        });
      }
    }
  }, [messages]);
  async function runToolCall(adapter_0: ClientToolExecutorAdapter, toolCallId_1: string, toolName_1: string, args_1: Record<string, unknown>) {
    const startedAt = Date.now();
    upsert(toolCallId_1, {
      toolName: toolName_1,
      status: "executing",
      startedAt
    });
    debug(`▶ executing ${toolName_1} (${toolCallId_1})`);
    let response: ClientToolResponse;
    let finalState: ClientToolState;
    try {
      const result = await adapter_0.execute(toolName_1, args_1);
      const completedAt_0 = Date.now();
      finalState = {
        toolCallId: toolCallId_1,
        toolName: toolName_1,
        status: "complete",
        startedAt,
        completedAt: completedAt_0
      };
      upsert(toolCallId_1, finalState);
      debug(`✓ ${toolName_1} completed in ${completedAt_0 - startedAt}ms`);
      response = {
        toolCallId: toolCallId_1,
        status: "success",
        result,
        executionTimeMs: completedAt_0 - startedAt
      };
    } catch (err) {
      const completedAt = Date.now();
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      debugError(`✗ ${toolName_1} failed in ${completedAt - startedAt}ms:`, errorMessage);
      finalState = {
        toolCallId: toolCallId_1,
        toolName: toolName_1,
        status: "error",
        startedAt,
        completedAt,
        error: errorMessage
      };
      upsert(toolCallId_1, finalState);
      response = {
        toolCallId: toolCallId_1,
        status: "error",
        error: errorMessage,
        executionTimeMs: completedAt - startedAt
      };
    }

    // Surface POST failures in dev: a dropped tool-results POST leaves the
    // workflow webhook waiting forever with no other client-side signal.
    const posted = await postToolResult({
      baseUrl: clientRef.current.baseUrl,
      fetch: clientRef.current.fetch,
      response
    }).catch(err_0 => {
      debugError(`tool-results POST network error for ${toolName_1}:`, err_0);
      return false;
    });
    if (!posted) {
      debugError(`tool-results POST was not accepted for ${toolName_1} (${toolCallId_1})`);
    }
    optionsRef.current?.onToolComplete?.(finalState);
  }
  return {
    activeTools
  };
}

// ---------------------------------------------------------------------------
// Deprecated aliases (plan 008 rename — see `use-magnus.ts`).
// ---------------------------------------------------------------------------

/** @deprecated Renamed — use `useMagnusTools`. */
export const useAgentTools = useMagnusTools;
/** @deprecated Renamed — use `UseMagnusToolsOptions`. */
export type UseAgentToolsOptions = UseMagnusToolsOptions;